import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api-client";
import { errText } from "@/admin/api";
import { NativeSelect } from "@/admin/components";

// A plan's insides: what it grants (entitlements) and how it is charged
// (price lines). Saving a grant pushes the new limits to subscribed tenants.

type Feature = { id: string; key: string; label: string; value_type: "limit" | "boolean" | "metered"; unit: string };
type Entitlement = {
  id: string;
  feature_id: string;
  feature_key: string;
  feature_label: string;
  value_type: Feature["value_type"];
  unit: string;
  limit_value: number | null;
  is_enabled: boolean;
};
type PriceLine = {
  id: string;
  kind: "flat" | "per_unit" | "metered";
  feature_id: string | null;
  feature_label: string | null;
  feature_unit: string | null;
  amount_cents: number;
  amount_millicents: number;
  currency: string;
  label: string;
  display_order: number;
  rate_millicents: number;
};
type Parts = {
  entitlements: Entitlement[];
  price_components: PriceLine[];
  features: Feature[];
  subscribers: number;
  notice: string | null;
};

const KINDS: [PriceLine["kind"], string][] = [
  ["flat", "Flat per month"],
  ["per_unit", "Per unit per month (× count)"],
  ["metered", "Metered (× usage, from the wallet)"],
];

function rateText(p: PriceLine) {
  const dollars = p.rate_millicents / 100000;
  const unit = (p.feature_label ?? "unit").toLowerCase().replace(/s$/, "");
  if (p.kind === "per_unit") return `$${dollars.toFixed(2)} per ${unit} / month`;
  if (p.kind === "metered") {
    const cents = p.rate_millicents / 1000;
    return `${cents < 100 ? `${cents}¢` : `$${dollars.toFixed(2)}`} per ${unit}`;
  }
  return `$${dollars.toFixed(2)} / month`;
}

export function PlanParts({ planId }: { planId: string }) {
  const qc = useQueryClient();
  const key = ["admin", "plan-parts", planId];
  const q = useQuery({ queryKey: key, queryFn: async () => (await api.get<Parts>(`/admin/plans/${planId}/parts/`)).data });
  const done = (p: Parts) => {
    qc.setQueryData(key, p);
    if (p.notice) toast.success(p.notice);
  };
  const useCall = <A,>(fn: (a: A) => Promise<{ data: Parts }>) =>
    useMutation({ mutationFn: async (a: A) => (await fn(a)).data, onSuccess: done, onError: (e) => toast.error(errText(e)) });

  const putGrant = useCall((b: { feature_id: string; limit_value: number | null; is_enabled: boolean }) =>
    api.put<Parts>(`/admin/plans/${planId}/entitlements/`, b),
  );
  const delGrant = useCall((id: string) => api.delete<Parts>(`/admin/plans/${planId}/entitlements/${id}/`));
  const addPrice = useCall((b: Partial<PriceLine>) => api.post<Parts>(`/admin/plans/${planId}/price-components/`, b));
  const savePrice = useCall(({ id, ...b }: Partial<PriceLine> & { id: string }) =>
    api.patch<Parts>(`/admin/plans/${planId}/price-components/${id}/`, b),
  );
  const delPrice = useCall((id: string) => api.delete<Parts>(`/admin/plans/${planId}/price-components/${id}/`));

  const [newFeature, setNewFeature] = useState("");

  if (!q.data) return null;
  const p = q.data;
  const unused = p.features.filter((f) => !p.entitlements.some((e) => e.feature_id === f.id));

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What the plan includes</CardTitle>
          <p className="text-sm text-muted-foreground">
            Empty limit = unlimited. Changes apply straight away to the {p.subscribers} tenant
            {p.subscribers === 1 ? "" : "s"} on this plan.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {p.entitlements.length === 0 && <p className="text-sm text-muted-foreground">Nothing yet.</p>}
          {p.entitlements.map((e) => (
            <GrantRow
              key={e.id}
              e={e}
              onSave={(limit_value, is_enabled) => putGrant.mutate({ feature_id: e.feature_id, limit_value, is_enabled })}
              onDelete={() => delGrant.mutate(e.id)}
            />
          ))}
          {unused.length > 0 && (
            <div className="flex items-center gap-2 border-t pt-3">
              <NativeSelect value={newFeature} onChange={setNewFeature} className="max-w-xs">
                <option value="">Add a feature…</option>
                {unused.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </NativeSelect>
              <Button
                variant="outline"
                disabled={!newFeature || putGrant.isPending}
                onClick={() => {
                  putGrant.mutate({ feature_id: newFeature, limit_value: null, is_enabled: true });
                  setNewFeature("");
                }}
              >
                <Plus className="mr-1 h-4 w-4" /> Add
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Price lines</CardTitle>
            <p className="text-sm text-muted-foreground">
              Per-unit lines are the monthly fee (e.g. per customer). Metered lines are taken from the prepaid wallet
              (e.g. per minute).
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => addPrice.mutate({ kind: "flat", amount_cents: 0 })}>
            <Plus className="mr-1 h-4 w-4" /> Add line
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {p.price_components.length === 0 && <p className="text-sm text-muted-foreground">No price lines.</p>}
          {p.price_components.map((line) => (
            <PriceRow
              key={line.id}
              line={line}
              features={p.features}
              onSave={(b) => savePrice.mutate({ id: line.id, ...b })}
              onDelete={() => delPrice.mutate(line.id)}
            />
          ))}
        </CardContent>
      </Card>
    </>
  );
}

function GrantRow({
  e,
  onSave,
  onDelete,
}: {
  e: Entitlement;
  onSave: (limit: number | null, enabled: boolean) => void;
  onDelete: () => void;
}) {
  const [limit, setLimit] = useState(e.limit_value == null ? "" : String(e.limit_value));
  const boolean = e.value_type === "boolean";
  const dirty = !boolean && limit !== (e.limit_value == null ? "" : String(e.limit_value));
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border p-3">
      <div className="min-w-[180px] flex-1">
        <div className="text-sm font-medium">{e.feature_label}</div>
        <div className="text-xs text-muted-foreground">
          {e.value_type === "boolean" ? "On / off" : e.value_type === "metered" ? "Included per month" : "Maximum"}
          {e.unit && ` · ${e.unit}`}
        </div>
      </div>
      {boolean ? (
        <Switch checked={e.is_enabled} onCheckedChange={(v) => onSave(null, v)} />
      ) : (
        <>
          <Input
            type="number"
            min={0}
            className="w-32"
            placeholder="Unlimited"
            value={limit}
            onChange={(ev) => setLimit(ev.target.value)}
          />
          <Button size="sm" disabled={!dirty} onClick={() => onSave(limit === "" ? null : Number(limit), e.is_enabled)}>
            Save
          </Button>
        </>
      )}
      <Button size="sm" variant="ghost" onClick={onDelete} title="Remove from plan">
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function PriceRow({
  line,
  features,
  onSave,
  onDelete,
}: {
  line: PriceLine;
  features: Feature[];
  onSave: (b: Partial<PriceLine>) => void;
  onDelete: () => void;
}) {
  const [form, setForm] = useState({
    label: line.label,
    kind: line.kind,
    feature_id: line.feature_id ?? "",
    amount_cents: String(line.amount_cents),
    amount_millicents: String(line.amount_millicents),
  });
  const dirty =
    form.label !== line.label ||
    form.kind !== line.kind ||
    form.feature_id !== (line.feature_id ?? "") ||
    form.amount_cents !== String(line.amount_cents) ||
    form.amount_millicents !== String(line.amount_millicents);
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold">{rateText(line)}</span>
        <Button size="sm" variant="ghost" onClick={onDelete} title="Delete line">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-5">
        <div className="space-y-1 md:col-span-2">
          <div className="text-xs text-muted-foreground">Label</div>
          <Input value={form.label} onChange={(e) => set("label")(e.target.value)} placeholder="e.g. Fee per customer" />
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Kind</div>
          <NativeSelect value={form.kind} onChange={set("kind")}>
            {KINDS.map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="space-y-1 md:col-span-2">
          <div className="text-xs text-muted-foreground">Counts</div>
          <NativeSelect value={form.feature_id} onChange={set("feature_id")} disabled={form.kind === "flat"}>
            <option value="">—</option>
            {features.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">Amount (cents)</div>
          <Input type="number" value={form.amount_cents} onChange={(e) => set("amount_cents")(e.target.value)} />
        </div>
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">+ millicents</div>
          <Input type="number" value={form.amount_millicents} onChange={(e) => set("amount_millicents")(e.target.value)} />
        </div>
        <div className="flex items-end md:col-span-3">
          <p className="text-xs text-muted-foreground">
            Cents + millicents make the rate: 2 cents + 500 millicents = 2.5¢. 5000 cents = $50.
          </p>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={!dirty}
          onClick={() =>
            onSave({
              label: form.label,
              kind: form.kind,
              feature_id: form.kind === "flat" ? null : form.feature_id || null,
              amount_cents: Number(form.amount_cents) || 0,
              amount_millicents: Number(form.amount_millicents) || 0,
            })
          }
        >
          Save line
        </Button>
      </div>
    </div>
  );
}
